import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DynamicFlowBaseUrlEnum, FlowBaseUrlEnum } from '@constants/app.constants';
import { ApiRequestType } from '@models/app.models';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { LowFareMonthyByDestination, LowFareMonthyByOrigin, LowFareSummaryRequest, SearchRequest } from '@models/requests/search-request.model';
import { ApiResponse } from '@models/responses/api-response.model';
import { N0AvailabilityResponse, N0LowFareAvailabilityByDestinationResponse, N0LowFareAvailabilityByOriginResponse, N0LowFareAvailabilitySummaryByOriginListResponse } from '@models/responses/availability-response.model';
import { N0LowFareTrip } from '@models/responses/lowfare-availability-response.model';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { ApiPendingRequestsService } from '@services/pending-requests/pending-requests.service';
import { finalize, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AvailabilityService {
  private static readonly CONTROLLER_ROOT = 'v1/availability';

  constructor(private httpClient: HttpClient, private readonly apiPendingRequestsService: ApiPendingRequestsService, private gbs: GrowthBookService) {}

  getAvailability(
    request: SearchRequest,
    path: FlowBaseUrlEnum | DynamicFlowBaseUrlEnum | null
  ): Observable<ApiResponse<N0AvailabilityResponse>> {
    let endpoint = `${EnvironmentService.environment.apiBaseUrl}${AvailabilityService.CONTROLLER_ROOT}/search`;
    if (this.gbs.isVariantOfExperimentEnabled('feat-001847-MMB-flights-not-including-low-fare-prices', 'b', '') && path === FlowBaseUrlEnum.MODIFY) {
      endpoint = endpoint + '/modify';
    }
    const clearBooking = request.clearBooking ? '?clear=true' : '';
    this.apiPendingRequestsService.increment(ApiRequestType.AvailabilitySearch);
    return this.httpClient.post<ApiResponse<N0AvailabilityResponse>>(
      endpoint + clearBooking,
      request,
    ).pipe(
      finalize(() => this.apiPendingRequestsService.decrement(ApiRequestType.AvailabilitySearch))
    );
  }

  getLowFareAvailability(
    request: SearchRequest,
    includePremium = true
  ): Observable<ApiResponse<N0LowFareTrip[]>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${AvailabilityService.CONTROLLER_ROOT}/lowfare?includePremium=${includePremium}`;
    return this.httpClient.post<ApiResponse<N0LowFareTrip[]>>(
      endpoint,
      request,
    );
  }

  getLowFareMonthlyByOriginAvailability(request: LowFareMonthyByOrigin): Observable<ApiResponse<N0LowFareAvailabilityByOriginResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${AvailabilityService.CONTROLLER_ROOT}/lowfare/monthly/origin`;
    return this.httpClient.post<ApiResponse<N0LowFareAvailabilityByOriginResponse>>(endpoint, request);
  }

  getLowFareMonthlyByDestinationAvailability(request: LowFareMonthyByDestination): Observable<ApiResponse<N0LowFareAvailabilityByDestinationResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${AvailabilityService.CONTROLLER_ROOT}/lowfare/monthly/destination`;
    return this.httpClient.post<ApiResponse<N0LowFareAvailabilityByDestinationResponse>>(endpoint, request);
  }

  getLowFareSearchByOriginList(request: LowFareSummaryRequest): Observable<ApiResponse<N0LowFareAvailabilitySummaryByOriginListResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${AvailabilityService.CONTROLLER_ROOT}/lowfare/summaries`;
    return this.httpClient.post<ApiResponse<N0LowFareAvailabilitySummaryByOriginListResponse>>(endpoint, request);
  }
}
